import FieldLoader from '@gef-ui/components/molecules/FieldLoader';
import PropTypes from 'prop-types';
import path from "ramda/es/path";
import identity from "ramda/es/identity";
import keys from "ramda/es/keys";
import o from "ramda/es/o";
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { callCodelistAPI, clearCodelistInStore, registerCodelist } from '../../actions';
import { codelistLoadingStates, howToHandDataOverToChildType } from '../../constants';
import { getCodelists } from '../../selectors';
import createCodelistApiSettingsDefinition from '../createCodelistApiSettingsDefinition';
import { withDefaultCodelistProps } from '../decorators';
const COMPONENT_NAME = 'CodelistContainer';
class CodelistContainer extends Component {
    constructor(props){
        super(props);
        this.codelistCalled = false;
        this.callCodelistAPIFn = props.callCodelistAPI(props.storeKey, props.oauth2 || path([
            'children',
            'props',
            'oauth2'
        ], props));
        this.clearCodelistInStoreFn = props.clearCodelistInStore(props.storeKey);
        if (!this.willWaitForParent() && !this.codelistCalled) {
            const { registerCodelist, codelist } = this.props;
            if (!codelist) {
                registerCodelist();
                this.callCodelist();
            }
        }
    }
    componentDidUpdate(prevProps) {
        const { waitForParentFilterValue, parentFilter, dispatch, storeKey } = prevProps;
        if (waitForParentFilterValue && parentFilter !== this.props.parentFilter) {
            dispatch(this.clearCodelistInStoreFn());
            this.props.onCodelistClear();
            this.codelistCalled = false;
        }
        if (storeKey !== this.props.storeKey) {
            const { callCodelistAPI: callCodelistAPIP, clearCodelistInStore: clearCodelistInStoreP, codelist, oauth2, storeKey, registerCodelist: registerCodelistP } = this.props;
            this.codelistCalled = false;
            this.callCodelistAPIFn = callCodelistAPIP(storeKey, oauth2 || path([
                'children',
                'props',
                'oauth2'
            ], this.props));
            this.clearCodelistInStoreFn = clearCodelistInStoreP(storeKey);
            if (!codelist || codelist.loading !== codelistLoadingStates.SUCCESS) {
                registerCodelistP();
                this.callCodelist();
            }
        }
    }
    callCodelist() {
        const { dispatch } = this.props;
        dispatch(this.callCodelistAPIFn(createCodelistApiSettingsDefinition(this.props)));
        this.codelistCalled = true;
    }
    willWaitForParent = ()=>this.props.waitForParentFilterValue && !this.props.parentFilter;
    reloadCodelistWhenParentFilterSet() {
        if (!this.codelistCalled && !this.willWaitForParent() && !!this.props.parentFilter) {
            this.callCodelist();
        }
    }
    willShowLoader(codelist) {
        const codelistAlreadyLoaded = codelist?.loading && (codelist.loading === codelistLoadingStates.FAIL || codelist.loading === codelistLoadingStates.SUCCESS);
        return !this.willWaitForParent() && !codelistAlreadyLoaded;
    }
    fillChildrenOfChild(codelist, convertDataIntoChildAcceptableFormat) {
        return codelist && !!codelist.values ? convertDataIntoChildAcceptableFormat(codelist.values) : undefined;
    }
    createChild(children, codelist) {
        const showLoader = this.willShowLoader(codelist);
        this.reloadCodelistWhenParentFilterSet();
        const { howToHandDataOverToChild, convertDataIntoChildAcceptableFormat, handDataOverToChildPropName } = this.props;
        const values = this.fillChildrenOfChild(codelist, convertDataIntoChildAcceptableFormat);
        const childDisabled = children.props.disabled ?? this.willWaitForParent();
        if (howToHandDataOverToChild === howToHandDataOverToChildType.CHILD) {
            return /*#__PURE__*/ React.createElement(FieldLoader, {
                isLoading: showLoader
            }, /*#__PURE__*/ React.cloneElement(children, {
                ...children.props,
                disabled: childDisabled
            }, values || []));
        } else if (howToHandDataOverToChild === howToHandDataOverToChildType.PROP) {
            return /*#__PURE__*/ React.createElement(FieldLoader, {
                isLoading: showLoader
            }, /*#__PURE__*/ React.cloneElement(children, {
                ...children.props,
                disabled: childDisabled,
                [handDataOverToChildPropName]: values || []
            }));
        }
    }
    render() {
        const { children, codelist } = this.props;
        return this.createChild(children, codelist);
    }
}
CodelistContainer.propTypes = {
    /**
	 * You can override behavior of whole codelist: which action will be called, way how to build codelist url. Example is part of GEF documentation.
	 */ callCodelistAPI: PropTypes.func,
    /**
	 * You can override default credentials settings for HTTP calls. You can do the same with callCodelistAPI but this way is simpler for this purpose.
	 */ callCodelistAPICredentials: PropTypes.string,
    /**
	 * Function for conversion of remote data that has for any reason different format
	 *
	 * @param rawCodelistResponse
	 * @return [codelistValues]
	 */ callCodelistApiRemoteDataConverter: PropTypes.func,
    /**
	 * Children will be rendered within the `Codelist`.
	 */ children: PropTypes.node.isRequired,
    /**
	 * You can override behavior of clearing codelist data in store before new call with changed parameters. It is used when parent value is changed so that codelist need to update its data.
	 */ clearCodelistInStore: PropTypes.func,
    /**
	 * Codelist object contains info about state and its values (codelist.loading and codelist.values).
	 */ codelist: PropTypes.object,
    /**
	 * You can override default codelist context defined in `state.urls.codelistContext` or default `code-table` context.
	 */ codelistContext: PropTypes.string,
    /**
	 * Function for codelist data conversion into acceptable format for child component.
	 *
	 * @param codelistValues
	 */ convertDataIntoChildAcceptableFormat: PropTypes.func,
    /**
	 * Component disabled
	 */ disabled: PropTypes.func,
    /**
	 * Redux dispatch pass on over connect decorator.
	 */ dispatch: PropTypes.func,
    /**
	 * You can override default codelist endpoint defined in `state.urls.codelist`.
	 */ endpoint: PropTypes.string,
    /**
	 * Need to be set when you choose howToHandDataOverToChild=PROP. It is a name of child's prop where you want to paste converted codelist data.
	 */ handDataOverToChildPropName: PropTypes.string,
    /**
	 * You can choose if converted codelist data will be propagated into child over props or as children. Default definition is propagation as children of child.
	 */ howToHandDataOverToChild: PropTypes.oneOf(keys(howToHandDataOverToChildType)),
    /**
	 * Codelist name.
	 */ name: PropTypes.string.isRequired,
    /**
	 * Turn on oauth2 request for codelist.
	 */ oauth2: PropTypes.bool,
    /**
	 * When switching parentFilter, codelist is cleaned in store. You can react on it.
	 */ onCodelistClear: PropTypes.func,
    /**
	 * Value against codelist values will be filtered on the BE.
	 */ parentFilter: PropTypes.string,
    /**
	 * You can override which name of url query string/param will be assigned to filter against `parent value`. Default is `filterByParent`.
	 */ parentFilterUrlParam: PropTypes.string,
    /**
	 * Register codelist to store before request data from API for detecting codelist before next same request.
	 */ registerCodelist: PropTypes.func,
    /**
	 * Attribute name under you find loaded codelist data in the store on path `state.codelists[storeKey]`.
	 */ storeKey: PropTypes.string.isRequired,
    /**
	 * Kind of verification parameter telling component that you want to wait codelist value after parent filter value will be set. False in default.
	 */ waitForParentFilterValue: PropTypes.bool
};
const mapStateToProps = (state, ownProps)=>({
        codelist: getCodelists(state)[ownProps.storeKey]
    });
const mapDispatchToProps = (dispatch, { storeKey })=>({
        dispatch,
        registerCodelist: bindActionCreators(registerCodelist(storeKey), dispatch)
    });
export { CodelistContainer };
export default o(connect(mapStateToProps, mapDispatchToProps), withDefaultCodelistProps(COMPONENT_NAME, {
    waitForParentFilterValue: false,
    parentFilterUrlParam: 'filterByParent',
    howToHandDataOverToChild: howToHandDataOverToChildType.CHILD,
    callCodelistAPI,
    clearCodelistInStore,
    registerCodelist,
    onCodelistClear: identity
}))(CodelistContainer);
